import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Img } from '@/components/ui';

/**
 * Poster collage with a lightbox, mirroring the "hero + rail" treatment used by
 * ticketing sites. Adapts to however many images the provider actually
 * uploaded — 1, 2, 3 or many — instead of assuming a fixed count.
 */
const Gallery = ({ images = [], title = '' }) => {
  const [lightbox, setLightbox] = useState(-1);
  const count = images.length;

  const close = useCallback(() => setLightbox(-1), []);
  const step = useCallback(
    (delta) => setLightbox((i) => (i < 0 ? i : (i + delta + count) % count)),
    [count]
  );

  useEffect(() => {
    if (lightbox < 0) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, close, step]);

  if (count === 0) return null;

  const [hero, ...rest] = images;

  return (
    <>
      <div className="grid gap-2 sm:grid-cols-4 sm:grid-rows-2 sm:gap-3">
        {/* Event art is almost always a portrait poster. Cropping it to 16:9
            cut the title off the top ("RAAS" instead of "GLITZ & RAAS"), so
            the poster is letterboxed whole over a blurred copy of itself. */}
        <button
          type="button"
          onClick={() => setLightbox(0)}
          aria-label={`View ${title} poster`}
          className={`lk-focus group relative aspect-[4/5] overflow-hidden rounded-2xl bg-ink-900 sm:aspect-auto ${
            count === 1 ? 'sm:col-span-4 sm:row-span-2' : 'sm:col-span-3 sm:row-span-2'
          }`}
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 scale-125 bg-cover bg-center opacity-70 blur-2xl"
            style={{ backgroundImage: `url("${hero}")` }}
          />
          <div aria-hidden="true" className="absolute inset-0 bg-black/15" />
          <Img
            src={hero}
            alt={title}
            fit="contain"
            className={`relative h-full w-full sm:h-auto ${count === 1 ? "sm:aspect-[16/9]" : "sm:aspect-[16/10]"}`}
            imgClassName="drop-shadow-2xl transition-transform duration-500 group-hover:scale-[1.02]"
          />
        </button>

        {rest.slice(0, 2).map((src, i) => {
          const isLast = i === 1;
          const hidden = count - 3;
          return (
            <button
              key={src}
              type="button"
              onClick={() => setLightbox(i + 1)}
              className="group relative hidden overflow-hidden rounded-2xl sm:block"
            >
              <Img
                src={src}
                alt={`${title} photo ${i + 2}`}
                className="h-full w-full"
                ratio="4 / 3"
                imgClassName="transition-transform duration-500 group-hover:scale-105"
              />
              {isLast && hidden > 0 && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-lg font-bold text-white backdrop-blur-[1px]">
                  +{hidden}
                </span>
              )}
            </button>
          );
        })}

        {/* Mobile: the extra shots become a swipeable strip. */}
        {rest.length > 0 && (
          <div className="scrollbar-hide -mx-4 flex gap-2 overflow-x-auto px-4 sm:hidden">
            {rest.map((src, i) => (
              <button
                key={src}
                type="button"
                onClick={() => setLightbox(i + 1)}
                className="w-28 shrink-0 overflow-hidden rounded-xl"
              >
                <Img src={src} alt={`${title} photo ${i + 2}`} ratio="1 / 1" />
              </button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox >= 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4"
          >
            <button
              type="button"
              aria-label="Close gallery"
              onClick={close}
              className="absolute right-4 top-4 rounded-full bg-white/10 p-2.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>

            {count > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous photo"
                  onClick={(e) => {
                    e.stopPropagation();
                    step(-1);
                  }}
                  className="absolute left-3 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 sm:left-8"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  aria-label="Next photo"
                  onClick={(e) => {
                    e.stopPropagation();
                    step(1);
                  }}
                  className="absolute right-3 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 sm:right-8"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            <motion.img
              key={images[lightbox]}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={images[lightbox]}
              alt={title}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full rounded-xl object-contain"
            />

            <p className="absolute bottom-5 text-sm text-white/70">
              {lightbox + 1} / {count}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;
